// client/src/routes/app/repairs/$ticketId.tsx — SCR-REP-002 · MAINT-007 (04 §8.9; the file
// path is §7's own tree, „app/repairs/$ticketId.tsx  # SCR-REP-002").
//
// THIS IS THE DEEP-LINK TARGET. ALR-003's „programează revizia"-shaped tasks for an incident
// point at a ticket, not at a filtered board, and 05 MAINT-006's criterion („completing a
// revision from the task deep-link works end-to-end") is the same requirement one screen over.
// The route reads its id from the URL and fetches that ticket, so a link opened cold — a fresh
// tab, an e-mail, a notification — lands on the right incident with every action available. The
// suite asserts exactly that, from a memory history with no navigation before it.
//
// ONE PAYLOAD FEEDS THE WHOLE SCREEN. The header's chips, the facts, the money, the gallery and
// every button come from `GET /repair-tickets/{id}`; `allowedStatuses` is the server's word on
// what may be done next (D-962), so nothing here re-derives the machine.
//
// EDITING IS INLINE rather than a fourth route, because a ticket's editable half is five fields
// and the server refuses the write on a closed ticket by name (`repair-ticket-closed`). The
// affordance follows that rule instead of restating it: no edit button on a DONE or CANCELLED
// ticket.
//
// „Editează" follows the ticket's apartment as well as the permission (BR-COOWN-09 rule 5,
// D-2465). `TicketActions` beside it already states who runs the flat when the caller does not,
// so the edit button simply falls away with the verbs rather than repeating the sentence.

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { usePropertyWrite } from '@/features/properties/administration';
import { isTerminal } from '@/features/repairs/enums';
import { useRepairErrorMessage } from '@/features/repairs/errors';
import { OverdueChip } from '@/features/repairs/RepairUrgencyChip';
import { useRepairTicket, useUpdateRepairTicket } from '@/features/repairs/queries';
import { toTicketInput, type TicketFormValues } from '@/features/repairs/schema';
import { TicketActions } from '@/features/repairs/TicketActions';
import { TicketDetail } from '@/features/repairs/TicketDetail';
import { TicketForm } from '@/features/repairs/TicketForm';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/repairs/$ticketId')({
  component: TicketScreen,
});

function TicketScreen() {
  const { ticketId } = Route.useParams();
  const { t } = useTranslation();
  const ticket = useRepairTicket(ticketId);
  const { canWrite } = usePropertyWrite(ticket.data?.propertyId, 'Operations.Write');
  const update = useUpdateRepairTicket();
  const describe = useRepairErrorMessage();

  const [editing, setEditing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (ticket.isPending) return <LoadingState rows={4} />;
  if (ticket.isError) {
    return <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />;
  }

  const detail = ticket.data;

  const submit = async (values: TicketFormValues) => {
    setFailure(null);
    try {
      await update.mutateAsync({
        path: { id: detail.id },
        // THE PHOTOS AND THE PROVIDER ARE CARRIED THROUGH, not dropped. The endpoint REPLACES
        // the attachment set and rewrites every field it is given, so posting `[]` and `null`
        // would delete the photographs a tenant sent from the portal and unassign the provider
        // the deviz named — neither of which the user asked for by editing a description.
        body: toTicketInput(
          values,
          detail.photos.map((photo) => photo.documentId),
          detail.serviceProviderId ?? null,
        ),
      });
      setEditing(false);
      toast.success(t('repairs.form.saved'));
    } catch (error) {
      setFailure(describe(error));
    }
  };

  return (
    <RequirePermission permission="Operations.Read">
      <DetailHeader
        backTo="/app/repairs"
        backLabel={t('repairs.detail.backToBoard')}
        glyph="wrench"
        title={detail.titleRo}
        subtitle={detail.propertyName ?? undefined}
        chips={
          <>
            <StatusBadge kind="RepairTicketStatus" value={detail.status} />
            {/* The server's „Depășit" (D-2473), beside the state it qualifies (D-2477). */}
            {detail.isOverdue ? <OverdueChip /> : null}
          </>
        }
        actions={
          editing ? undefined : (
            <>
              {canWrite && !isTerminal(detail.status) ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    setFailure(null);
                    setEditing(true);
                  }}
                >
                  <Icon name="cog" className="h-4 w-4" />
                  {t('repairs.actions.edit')}
                </button>
              ) : null}
              <TicketActions ticket={detail} />
            </>
          )
        }
      />

      {editing ? (
        <TicketForm
          ticket={detail}
          submitting={update.isPending}
          failure={failure}
          onSubmit={(values) => void submit(values)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <TicketDetail ticket={detail} />
      )}
    </RequirePermission>
  );
}
